import {
  createSlice,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { RootState } from "state/store";
import type { PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";
import { parseJwt, loadState } from "utils";
import type { TokenUserType, MfaMethods } from "services/auth-service";

interface AuthState {
  tenantId: number | null;
  adminId: number | null;
  username: string | null;
  atok: string | null;
  exp: number | null;
  scope: string[];
  adminName: string;
  emailAddress: string;
  passwordExpired: boolean;
  type: TokenUserType | null;
  mfaMethods: MfaMethods[];
  t: number;
}

interface setAuthPayload {
  accessToken: string;
  passwordExpired?: boolean;
  type: TokenUserType;
  mfaMethods: MfaMethods[];
}

interface setUserDetailPayload {
  emailAddress: string;
  adminName: string;
  changePasswordRequired: boolean;
}

export const defaultState: AuthState = {
  tenantId: null,
  adminId: null,
  username: null,
  atok: null,
  exp: null,
  scope: [],
  adminName: "",
  emailAddress: "",
  passwordExpired: false,
  type: null,
  mfaMethods: [],
  t: 0,
};
const storedState = loadState("auth", Object.keys(defaultState)) as AuthState;
const IsExpiringTime = 300;

export const setT = createAsyncThunk(
  "auth/setT",
  async (args: { funcName: string }, { getState }) => {
    const promise = new Promise<{ funcName: string; state: RootState }>(
      (resolve, reject) => {
        resolve({
          funcName: args.funcName,
          state: getState() as RootState,
        });
      }
    );
    return promise;
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState: Object.keys(storedState).length ? storedState : defaultState,
  reducers: {
    setAuth: (state, action: PayloadAction<setAuthPayload>) => {
      state.atok = action.payload.accessToken;
      const content = parseJwt(state.atok);
      const type = content.type || action.payload.type || null;
      if (!!type) {
        state.exp = parseInt(content.exp);
        state.tenantId = content.tid;
        state.adminId = content.uid;
        state.username = content.uname;
        state.passwordExpired = !!action.payload.passwordExpired;
        state.type = type;
        state.mfaMethods = action.payload.mfaMethods ?? [];
        if (type === "PRE_ADMIN") {
          state.scope = [];
        } else if (type === "ADMIN") {
          state.scope = content.scope ? content.scope.split(" ") : [];
        }
      }
    },
    clearAuth: (state) => {
      state.tenantId = null;
      state.adminId = null;
      state.username = null;
      state.atok = null;
      state.exp = null;
      state.scope = [];
      state.adminName = "";
      state.emailAddress = "";
      state.passwordExpired = false;
      state.type = null;
      state.mfaMethods = [];
      state.t = 0;
    },
    setUserDetail: (state, action: PayloadAction<setUserDetailPayload>) => {
      state.emailAddress = action.payload.emailAddress ?? "";
      state.adminName = action.payload.adminName ?? "";
      // if (action.payload.changePasswordRequired !== undefined) {
      //   state.passwordExpired = action.payload.changePasswordRequired;
      // }
    },
    updateMfaMethods: (state, action: PayloadAction<MfaMethods[]>) => {
      state.mfaMethods = action.payload;
    },
    // setT: (state) => {
    //   state.t = Number(moment().unix());
    // },
  },
  extraReducers: (builder) => {
    builder.addCase(setT.fulfilled, (state, action) => {
      state.t = Number(moment().unix());
    });
  },
});

const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectAuthState = createSelector([selectAuth], (s: AuthState) => {
  const isATokNotExpired = s.exp !== null ? s.exp - s.t > 0 : false;
  const mfaAuthenticated = s.type !== "PRE_ADMIN";
  return {
    tenantId: s.tenantId,
    adminId: s.adminId,
    username: s.username,
    atok: s.atok,
    scope: s.scope,
    adminName: s.adminName,
    emailAddress: s.emailAddress,
    isAuthenticated: !!s.atok && isATokNotExpired,
    mfaAuthenticated: mfaAuthenticated,
    mfaMethods: s.mfaMethods,
    refreshRequired: !!s.atok && !isATokNotExpired,
    passwordExpired: s.passwordExpired,
    remain: s.exp !== null ? (s.exp - s.t) : 0,
  };
});

export const { setAuth, clearAuth, setUserDetail, updateMfaMethods } =
  authSlice.actions;

export default authSlice.reducer;
